/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

export interface DateTimeValue {
  localDate: Date;
  isoString: string;
  timezone: string;
}

export interface TimeValue {
  hours: number;
  minutes: number;
  period: "AM" | "PM";
}

export const createDateTimeValue = (
  date: Date | null
): DateTimeValue | null => {
  if (!date) return null;

  return {
    localDate: date,
    isoString: date.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const parseLocalDate = (value: string | Date): Date => {
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(value + "T00:00:00");
  }
  return new Date(value);
};

export const setDateOnly = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const setTimeOnly = (
  date: Date,
  hours: number,
  minutes: number
): Date => {
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
};

export const combineDateTime = (
  dateValue: Date,
  timeHours: number,
  timeMinutes: number
): Date => {
  const combined = new Date(dateValue);
  combined.setHours(timeHours, timeMinutes, 0, 0);
  return combined;
};

export const extractTime = (date: Date): { hours: number; minutes: number } => {
  return {
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
};

export const formatDateDisplay = (date: Date, timeZone?: string): string => {
  const currentYear = new Date().getFullYear();
  const dateYear = Number(
    date.toLocaleDateString("en-US", {
      year: "numeric",
      ...(timeZone ? { timeZone } : {}),
    })
  );
  const showYear = dateYear !== currentYear;

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(showYear && { year: "numeric" }),
    ...(timeZone ? { timeZone } : {}),
  });
};

export const formatDateRangeDisplay = (
  startDate?: string | Date,
  endDate?: string | Date
): string | null => {
  if (!startDate && !endDate) return null;

  const currentYear = new Date().getFullYear();

  const formatSingleDate = (date: Date, forceYear: boolean): string => {
    const showYear = forceYear || date.getFullYear() !== currentYear;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(showYear && { year: "numeric" }),
    });
  };

  const start = startDate ? parseLocalDate(startDate) : null;
  const end = endDate ? parseLocalDate(endDate) : null;

  if (!end) return formatSingleDate(start!, false);
  if (!start) return formatSingleDate(end, false);

  if (start.toDateString() === end.toDateString()) {
    return formatSingleDate(end, false);
  }

  const startYearDifferent = start.getFullYear() !== currentYear;
  const endYearDifferent = end.getFullYear() !== currentYear;
  const yearsAreDifferent = start.getFullYear() !== end.getFullYear();

  return `${formatSingleDate(start, startYearDifferent || yearsAreDifferent)} - ${formatSingleDate(end, endYearDifferent)}`;
};

export const formatTimeDisplay = (
  hours: number,
  minutes: number,
  period: "AM" | "PM"
): string => {
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${hours}:${formattedMinutes} ${period}`;
};

export const convertTo12Hour = (
  hour24: number
): { hour12: number; period: "AM" | "PM" } => {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return { hour12, period };
};

export const convertTo24Hour = (
  hour12: number,
  period: "AM" | "PM"
): number => {
  if (period === "AM") {
    return hour12 === 12 ? 0 : hour12;
  } else {
    return hour12 === 12 ? 12 : hour12 + 12;
  }
};

export const isSameDay = (
  date1: Date | null,
  date2: Date | null,
  timeZone?: string
): boolean => {
  if (!date1 || !date2) return false;
  if (timeZone) {
    return dayKeyInZone(date1, timeZone) === dayKeyInZone(date2, timeZone);
  }
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

// Calendar day (YYYY-MM-DD) of an instant within a specific IANA zone.
export const dayKeyInZone = (date: Date, timeZone: string): string =>
  date.toLocaleDateString("en-CA", { timeZone });

export const isDateDisabled = (
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  disabledDates?: Date[]
): boolean => {
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (minDate) {
    const minDateOnly = new Date(
      minDate.getFullYear(),
      minDate.getMonth(),
      minDate.getDate()
    );
    if (dateOnly < minDateOnly) return true;
  }

  if (maxDate) {
    const maxDateOnly = new Date(
      maxDate.getFullYear(),
      maxDate.getMonth(),
      maxDate.getDate()
    );
    if (dateOnly > maxDateOnly) return true;
  }

  if (disabledDates?.some((d) => isSameDay(d, date))) return true;

  return false;
};

export const getTimezoneOffset = (): string => {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? "+" : "-";
  return `GMT${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

export const getTimezoneAbbreviation = (): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = timezone.split("/");
  return (parts[parts.length - 1] ?? timezone).replace("_", " ");
};

export const getSmartStartTime = (minutesIncrement: number = 30): TimeValue => {
  const now = new Date();
  const nextHourIn24 = now.getHours() + 1;
  const roundedMinutes = 0;

  const { hour12, period } = convertTo12Hour(nextHourIn24);
  return { hours: hour12, minutes: roundedMinutes, period };
};

export const addHoursToTime = (
  time: TimeValue,
  hoursToAdd: number
): TimeValue => {
  const hour24 = convertTo24Hour(time.hours, time.period);
  const newHour24 = (hour24 + hoursToAdd) % 24;
  const { hour12, period } = convertTo12Hour(newHour24);

  return { hours: hour12, minutes: time.minutes, period };
};

export const toLocalDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export function formatMmmDdYyyy(dateString?: string) {
  if (!dateString) return "";
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function isEventPast(endTimeIso?: string): boolean {
  if (!endTimeIso) return false;
  const endTime = new Date(endTimeIso);
  return endTime.getTime() < Date.now();
}

export function isExpired(dateString?: string | null): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}

export function getMonthRangeISO(date: Date): {
  startTime: string;
  endTime: string;
} {
  const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}

export function getWeekRangeISO(date: Date): {
  startTime: string;
  endTime: string;
} {
  const dayOfWeek = date.getDay();
  const start = new Date(date);
  start.setDate(date.getDate() - dayOfWeek);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}

export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function formatDistanceToNow(dateString: string): string {
  return formatTimeAgo(dateString);
}

export function formatShortTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** A start time in the zone it is shown in, with the zone's short name. */
export function zonedTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/** A date as a glance reads it: "Oct 28". */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function shortDateTime(iso: string | null): string {
  return iso === null
    ? "Not yet"
    : new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

export type TypedDigit = { value: number | null; pending: string; done: boolean };

/**
 * One keystroke in a two-digit time field whose text is selected, so each key arrives alone. A digit
 * that could still start a longer number waits as `pending`: in hours, "1" then "0" is 10, and "0"
 * then "8" is 8. `value` is null when the digits so far are not a value yet.
 */
export const typeTimeDigit = (
  pending: string,
  typed: string,
  min: number,
  max: number,
): TypedDigit => {
  const inRange = (value: number) => value >= min && value <= max;
  const digits = typed.replace(/\D/g, "");
  if (digits.length >= 2) {
    const whole = parseInt(digits.slice(-2), 10);
    return { value: inRange(whole) ? whole : null, pending: "", done: inRange(whole) };
  }
  if (digits === "") {
    return { value: null, pending, done: false };
  }
  const joined = parseInt(pending + digits, 10);
  if (pending !== "" && inRange(joined)) {
    return { value: joined, pending: "", done: true };
  }
  const digit = parseInt(digits, 10);
  const value = inRange(digit) ? digit : null;
  return digit * 10 <= max ? { value, pending: digits, done: false } : { value, pending: "", done: true };
};
